import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  handleAddEditParams,
  IAddParams,
  IEditParams,
  mapToAddEditParams,
  routeParamWithPrefix,
} from 'src/app/lego-utils';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EMPTY, from, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AddOrEditResponsibleUserDrawerComponent } from 'src/app/components/main-content/design-lookup/design-data/add-or-edit-responsible-user/add-or-edit-responsible-user-drawer.component';
import { Design } from 'src/app/models/domain';
import { DesignService } from 'src/app/state';

const prefix = 'design';
@Component({
  selector: 'em-design-data',
  styleUrls: ['./design-data.component.less'],
  templateUrl: './design-data.component.html',
  standalone: false,
})
export class DesignDataComponent implements OnInit {
  public readonly designRouteParam = routeParamWithPrefix(prefix);

  @Input() public design?: Design;
  @Input() public hasWarning?: boolean;

  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly drawerService: NzDrawerService,
    private readonly notification: NzNotificationService,
    private readonly designService: DesignService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.add(
      this.route.queryParams
        .pipe(
          map((params) => mapToAddEditParams(params, prefix)),
          // prevents opening multiple drawers if other params are changed
          distinctUntilChanged<IAddParams | IEditParams | undefined>(
            (previous, current) => current?.mode === previous?.mode
          ),
          filter((params): params is IAddParams | IEditParams => !!params),
          switchMap((params) => {
            const afterClose$ = handleAddEditParams(
              params,
              () => EMPTY,
              (designId: string) =>
                this.drawerService.create({
                  nzContent: AddOrEditResponsibleUserDrawerComponent,
                  nzContentParams: { designId },
                  nzTitle: `Update Responsible User`,
                  nzWidth: 580,
                }).afterClose
            );

            return afterClose$ ?? EMPTY;
          }),
          switchMap(() => {
            if (this.design) {
              return this.designService.loadByDesignId(this.design.designId);
            }

            return EMPTY;
          }),
          switchMap(() => {
            return from(
              this.router.navigate([], {
                queryParams: this.designRouteParam.clear,
                queryParamsHandling: 'merge',
                // TODO we should check if this will result in duplicate history and rewrite the history
                // example: /foo -> /foo/edit/1 -> /foo will cause the history to have [/foo, /foo]
                // if we use skipLocationChange on /foo/edit/1 then the users won't be able to deep link
                replaceUrl: true,
              })
            );
          })
        )
        .subscribe({
          error: () => {
            this.notification.error(
              'An error occurred',
              `Failed to update URL after drawer was closed`
            );
          },
        })
    );
  }
}
